import React from "react";
import { X, CheckCircle2, MinusCircle, AlertTriangle, ShieldAlert, ArrowRight } from "lucide-react";

const CHIP = {
  ok: { bg: "rgba(15,185,153,0.14)", fg: "#067a66", Icon: CheckCircle2, word: "met" },
  planned: { bg: "rgba(64,96,255,0.12)", fg: "#2f46c9", Icon: ArrowRight, word: "would run" },
  blocked: { bg: "rgba(180,83,9,0.14)", fg: "#B45309", Icon: ShieldAlert, word: "blocked" },
  stop: { bg: "rgba(180,83,9,0.14)", fg: "#B45309", Icon: MinusCircle, word: "stopped here" },
  skipped: { bg: "rgba(15,18,38,0.06)", fg: "#5b6076", Icon: MinusCircle, word: "nothing to do" },
  not_reached: { bg: "rgba(15,18,38,0.04)", fg: "#8a8fa3", Icon: MinusCircle, word: "not reached" },
  failed: { bg: "rgba(220,38,38,0.12)", fg: "#DC2626", Icon: AlertTriangle, word: "failed" },
};

const STATUS_LINE = {
  planned: "Worked out and ready — nothing moves until you approve it in your wallet",
  blocked: "Everything is worked out, but no wallet here can sign yet",
  stopped: "A condition said no, so nothing ran",
  nothing_to_do: "Nothing worth moving this time",
  failed: "Something in the recipe is broken",
};

export const RunDetail = ({ run, onClose }) => {
  if (!run) return null;
  return (
    <div className="fixed inset-0 z-[310] flex items-start justify-center p-4 overflow-y-auto" data-testid="rpa-run-detail">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.45)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[680px] my-8 rounded-2xl overflow-hidden fade-up"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        <div className="px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
          <div className="flex items-center gap-3">
            <div>
              <h3 className="font-display text-xl font-semibold tracking-tight">{run.recipe_name}</h3>
              <div className="text-[12px] text-ink-3 mt-0.5">
                {run.trigger === "schedule" ? "on schedule" : "run by hand"} · {String(run.ran_at).slice(0, 16).replace("T", " ")} UTC
                {run.mode === "dry_run" ? " · dry run" : " · live"}
              </div>
            </div>
            <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5"
              data-testid="rpa-run-close"><X size={16} /></button>
          </div>
          <div className="mt-3 text-sm" data-testid="rpa-run-status">
            <b>{STATUS_LINE[run.status] || run.status}</b>
            {run.would_move > 0 && <> — <b>{run.would_move} CA</b> in total.</>}
            {run.stopped_because && <div className="text-ink-2 mt-1">{run.stopped_because}.</div>}
          </div>
          {run.data_as_of && (
            <div className="text-[11.5px] text-ink-3 mt-2">
              Balances as at {String(run.data_as_of).slice(0, 16).replace("T", " ")} UTC — the last CaryPact sync.
            </div>
          )}
        </div>

        <div className="px-6 py-4 space-y-2.5">
          {(run.steps || []).map((s, i) => {
            const c = CHIP[s.status] || CHIP.skipped;
            return (
              <div key={s.step_id || i} className="glass p-3.5" data-testid={`rpa-run-step-${i}`}>
                <div className="flex items-start gap-2.5">
                  <c.Icon size={15} className="mt-0.5 shrink-0" style={{ color: c.fg }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{s.label}</span>
                      <span className="text-[12px] text-ink-3">{s.wallet}</span>
                      <span className="chip ml-auto" style={{ background: c.bg, color: c.fg }}>{c.word}</span>
                    </div>
                    <div className="text-[12.5px] text-ink-2 mt-1">{s.detail}</div>
                    {s.would_call && (
                      <pre className="mt-2 text-[11px] font-mono p-2.5 rounded-xl overflow-x-auto"
                        style={{ background: "rgba(15,18,38,0.04)", color: "#4a4f66" }}
                        data-testid={`rpa-run-call-${i}`}>
{s.would_call.endpoint}
{JSON.stringify(s.would_call.payload, null, 1)}
                      </pre>
                    )}
                    {s.signer && !s.signer.can_sign && (
                      <div className="text-[11.5px] mt-1.5" style={{ color: "#B45309" }}>
                        Waiting on your wallet to approve this{s.signer.token_expired ? " · its CaryPact connection has expired too, so re-connect it first" : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
